import { useEffect, useMemo, useState } from "react";
import { useRealtime } from "@/contexts/RealtimeContext";
import { Badge } from "@/components/ui/badge";
import { Bell, Radio, WifiOff, CloudOff, Users } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useProperty } from "@/contexts/PropertyContext";

/**
 * The pulsing "Live" pill that sits in every shell header.
 * Shows connection status, pending offline writes, and per-property presence.
 */
export function LiveIndicator() {
  const { status, pending, presence } = useRealtime();
  const { propertyId } = useProperty();
  const [pulse, setPulse] = useState(false);

  const propUsers = useMemo(() => (presence[propertyId] || []), [presence, propertyId]);
  const uniqueRoles = useMemo(() => {
    const s = new Set(propUsers.map((u) => u.role));
    return Array.from(s);
  }, [propUsers]);

  useEffect(() => {
    if (status !== "online") return;
    const id = setInterval(() => setPulse((p) => !p), 1600);
    return () => clearInterval(id);
  }, [status]);

  const tone = {
    online: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
    connecting: "border-amber-500/40 text-amber-300 bg-amber-500/10",
    reconnecting: "border-amber-500/40 text-amber-300 bg-amber-500/10",
    offline: "border-rose-500/40 text-rose-300 bg-rose-500/10",
    idle: "border-white/10 text-zinc-400 bg-white/5",
  }[status] || "border-white/10 text-zinc-400 bg-white/5";

  const label = {
    online: "Live",
    connecting: "Connecting…",
    reconnecting: "Reconnecting…",
    offline: "Offline",
    idle: "Idle",
  }[status] || status;

  const Icon = status === "offline" ? WifiOff : status === "online" ? Radio : Radio;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center gap-2" data-testid="live-indicator">
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge variant="outline" className={"rounded-full px-2.5 py-1 flex items-center gap-1.5 " + tone} data-testid={`live-status-${status}`}>
              <span className="relative inline-flex h-2 w-2">
                {status === "online" && (
                  <span className={"absolute inline-flex h-full w-full rounded-full bg-emerald-400/60 " + (pulse ? "animate-ping" : "")} />
                )}
                <span className={"relative inline-flex rounded-full h-2 w-2 " + (
                  status === "online" ? "bg-emerald-400" :
                  status === "offline" ? "bg-rose-400" :
                  "bg-amber-400"
                )} />
              </span>
              <Icon className="h-3 w-3" />
              <span className="text-[11px] uppercase tracking-widest">{label}</span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="bg-zinc-950 border-white/10 text-xs">
            <div className="font-medium">Realtime {status}</div>
            <div className="text-zinc-400">Bids, RFPs, notifications, and alerts sync live via secure WebSocket.</div>
          </TooltipContent>
        </Tooltip>

        {pending > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10 rounded-full px-2 py-1 flex items-center gap-1" data-testid="live-pending">
                <CloudOff className="h-3 w-3" />
                <span className="text-[11px] tabular-nums">{pending}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-zinc-950 border-white/10 text-xs">
              {pending} change{pending === 1 ? "" : "s"} pending — will sync when you&apos;re online.
            </TooltipContent>
          </Tooltip>
        )}

        {propUsers.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 bg-indigo-500/10 rounded-full px-2 py-1 flex items-center gap-1" data-testid="live-presence">
                <Users className="h-3 w-3" />
                <span className="text-[11px] tabular-nums">{propUsers.length}</span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="bg-zinc-950 border-white/10 text-xs max-w-xs">
              <div className="font-medium">Online right now ({uniqueRoles.join(", ")})</div>
              <div className="text-zinc-400 mt-1 space-y-0.5">
                {propUsers.slice(0, 8).map((u) => (
                  <div key={u.user_id} className="truncate">{u.name} <span className="text-zinc-500">· {u.role}</span></div>
                ))}
                {propUsers.length > 8 && <div className="text-zinc-500">+{propUsers.length - 8} more</div>}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * A tiny bell that renders unread notifications count and pulses when new ones arrive live.
 */
export function LiveNotificationsBell({ onClick }) {
  const { subscribe } = useRealtime();
  const [count, setCount] = useState(0);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    // Load initial unread count.
    let cancelled = false;
    (async () => {
      try {
        const { api } = await import("@/lib/api");
        const { data } = await api.get("/notifications");
        const unread = (data.items || []).filter((n) => !n.read).length;
        if (!cancelled) setCount(unread);
      } catch (err) { if (typeof console !== 'undefined') console.debug('components/LiveIndicator.jsx: swallowed', err); }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const unsub = subscribe("*", ({ event }) => {
      if (event === "notification.new") {
        setCount((c) => c + 1);
        setFlash(true);
        setTimeout(() => setFlash(false), 1200);
      }
    });
    return unsub;
  }, [subscribe]);

  return (
    <button
      onClick={onClick}
      data-testid="live-bell"
      className={"relative rounded-full h-9 w-9 flex items-center justify-center border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] transition " + (flash ? "ring-2 ring-orange-400" : "")}
      aria-label="Notifications"
    >
      <Bell className={"h-4 w-4 " + (flash ? "text-orange-300" : "text-zinc-300")} />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] flex items-center justify-center tabular-nums" data-testid="live-bell-count">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
